import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gallery1 from "../assets/gallery1.avif";
import gallery2 from "../assets/gallery2.avif";
import gallery3 from "../assets/gallery3.avif";
import cateringImage from "../assets/catering-image.avif";
import { Helmet } from "react-helmet-async";
import { useUser } from "../context/UserContext";

import { useApi } from "../utils/Api";

const heroBanner = "/assets/hero-banner.avif";

const arrowBtnCls = "bg-brand-orange text-black text-[22px] w-12 h-12 rounded-full cursor-pointer transition-all duration-300 border-none disabled:opacity-20 hover:bg-[#facc15] hover:text-black max-[768px]:hidden";
const highlightTextCls = "text-brand-orange font-bold uppercase mt-6 mb-2 text-base tracking-wide";

const HomePage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPrevGalleryDisabled, setIsPrevGalleryDisabled] = useState(true);
  const [isNextGalleryDisabled, setIsNextGalleryDisabled] = useState(false);
  const galleryWrapperRef = useRef(null);

  const wrapperRef = useRef(null);
  const mapSectionRef = useRef(null);
  const [showMap, setShowMap] = useState(false);
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const { openLoginModal, user } = useUser();

  // Only mount the Google Maps iframe once its section scrolls into view,
  // so it never competes with the initial page load.
  useEffect(() => {
    const node = mapSectionRef.current;
    if (!node || showMap) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowMap(true);
          observer.disconnect();
        }
      },
      { rootMargin: "1000px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [showMap]);

  const images = [
    { src: gallery1, alt: "Grilled Fish with Plantain" },
    { src: gallery2, alt: "Assorted Meat Peppersoup" },
    { src: gallery3, alt: "Nigerian Rice and Stew" },
  ];

  useEffect(() => {
    const fetchMenuItems = async () => {
      try { const data = await apiFetch("fetch-random-items"); setMenuItems(data); }
      catch (error) { console.error("Error fetching menu:", error); }
    };
    fetchMenuItems();
  }, [apiFetch]);

  const itemsWithViewAll = Array.isArray(menuItems)
    ? [...menuItems, { id: "view-all", viewAll: true }]
    : [{ id: "view-all", viewAll: true }];

  const scrollAmount = 300;

  const handleScroll = () => {
    if (!wrapperRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = wrapperRef.current;
    setIsPrevDisabled(scrollLeft <= 0);
    setIsNextDisabled(scrollLeft + clientWidth >= scrollWidth - 5);
  };

  const nextItem = () => { if (wrapperRef.current) { wrapperRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" }); setTimeout(handleScroll, 400); } };
  const prevItem = () => { if (wrapperRef.current) { wrapperRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" }); setTimeout(handleScroll, 400); } };

  useEffect(() => { window.history.scrollRestoration = "manual"; }, []);
  useEffect(() => { if (menuItems.length > 0 && wrapperRef.current) { wrapperRef.current.scrollLeft = 0; handleScroll(); } }, [menuItems]);

  const handleOrder = () => { window.location.href = "/order-online"; };
  const handleViewMenu = () => { navigate("/order-online"); };

  const galleryScrollAmount = 300;
  const handleGalleryScroll = () => {
    if (!galleryWrapperRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = galleryWrapperRef.current;
    setIsPrevGalleryDisabled(scrollLeft <= 0);
    setIsNextGalleryDisabled(scrollLeft + clientWidth >= scrollWidth - 5);
  };
  const nextGalleryItem = () => { if (galleryWrapperRef.current) { galleryWrapperRef.current.scrollBy({ left: galleryScrollAmount, behavior: "smooth" }); setTimeout(handleGalleryScroll, 400); } };
  const prevGalleryItem = () => { if (galleryWrapperRef.current) { galleryWrapperRef.current.scrollBy({ left: -galleryScrollAmount, behavior: "smooth" }); setTimeout(handleGalleryScroll, 400); } };
  useEffect(() => { handleGalleryScroll(); }, []);

  return (
    <div className="bg-black mt-[100px] max-lg:mt-[75px]">
      <Helmet>
        <title>LasGidi Restaurant | Authentic Nigerian Cuisine in Manchester</title>
        <meta name="description" content="LasGidi Restaurant offers authentic Nigerian cuisine in Manchester. Explore our menu, book a table, or order online for delicious meals." />
        <meta name="keywords" content="LasGidi Restaurant, Nigerian Cuisine, Manchester Restaurant, Food Delivery, Buffet, Event Halls, Book Table" />
        <meta property="og:title" content="LasGidi Restaurant | Authentic Nigerian Cuisine in Manchester" />
        <meta property="og:description" content="LasGidi Restaurant offers authentic Nigerian cuisine in Manchester. Explore our menu, book a table, or order online for delicious meals." />
        <meta property="og:type" content="website" />
        <link rel="preload" as="image" href={heroBanner} fetchpriority="high" />
      </Helmet>

      {/* ===== HERO SECTION ===== */}
      <section className="relative w-full h-[450px] standard-padding flex items-center justify-center max-[480px]:h-[320px]">
        <img 
          src={heroBanner} 
          alt="LasGidi Restaurant Hero" 
          className="absolute inset-0 w-full h-full object-cover"
          fetchpriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/20" /> {/* Subtle overlay for better contrast */}
        {/* Order box container */}
        <div className="absolute top-[90%] left-0 w-full standard-padding max-[480px]:top-[85%]">
          <div className="flex items-center bg-black border border-[#2b2b2b] rounded-xl px-6 py-4 w-full shadow-[rgba(255,255,255,0.1)_0px_8px_24px_0px] relative text-right justify-end max-[480px]:shadow-lg max-[480px]:justify-center">
            <div>
              <button
                className="bg-brand-orange text-black font-bold text-base border-none rounded-lg px-10 py-4 cursor-pointer transition-all duration-300 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] font-[inherit] max-[480px]:w-full"
                onClick={handleOrder}
              >
                Order Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Login link */}
      {!user ? (
        <p className="mt-[100px] text-sm text-center max-[480px]:mt-[80px]">
          <button type="button" onClick={() => openLoginModal()} className="bg-transparent border-none text-white underline decoration-dashed decoration-brand-orange cursor-pointer font-[inherit] text-sm p-0">Log in</button>{" "}
          to view your recent orders
        </p>
      ) : (
        <p className="mt-[100px] text-sm text-center max-[480px]:mt-[80px]">
          <button type="button" onClick={() => navigate("/profile?tab=orders")} className="bg-transparent border-none text-white underline decoration-dashed decoration-brand-orange cursor-pointer font-[inherit] text-sm p-0">View Recent Orders</button>
        </p>
      )}



      {/* ===== MENU SECTION ===== */}
      <section className="bg-black text-white py-[60px] overflow-hidden standard-padding">
        <div className="flex flex-wrap gap-5 justify-between items-center mx-auto mb-[30px]">
          <h2 className="text-3xl font-bold font-poppins">Our menu</h2>
          <div className="flex items-center gap-2.5">
            <button
              className="border-2 border-brand-orange bg-transparent text-brand-orange font-semibold text-lg rounded cursor-pointer transition-colors duration-300 px-4 py-2.5 mr-5 font-[inherit] hover:bg-[#facc15] hover:text-black hover:border-[#facc15] max-[768px]:hidden"
              onClick={handleViewMenu}
            >
              View Full Menu
            </button>
            <button className={arrowBtnCls} onClick={prevItem} disabled={isPrevDisabled}>&#10094;</button>
            <button className={arrowBtnCls} onClick={nextItem} disabled={isNextDisabled}>&#10095;</button>
          </div>
        </div>

        <div
          className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory gap-5 mt-3 scrollbar-hide pt-4 pb-6 px-1"
          ref={wrapperRef}
          onScroll={handleScroll}
        >
          {itemsWithViewAll.map((item) =>
            item.viewAll ? (
              <div
                className="flex-[0_0_230px] max-w-[230px] flex items-center justify-center overflow-hidden transition-transform duration-300 bg-black p-2.5 border border-brand-orange rounded-[10px] cursor-pointer snap-start hover:-translate-y-1"
                key="view-all"
                onClick={handleViewMenu}
              >
                <div className="py-[15px]">
                  <h3 className="text-base font-semibold text-brand-orange flex items-center gap-1.5">
                    View Full Menu <span className="text-[1.2rem] transition-transform duration-300 hover:translate-x-1">&gt;</span>
                  </h3>
                </div>
              </div>
            ) : (
              <div className="flex-[0_0_230px] max-w-[230px] overflow-hidden transition-[transform,box-shadow] duration-300 bg-black p-2.5 border border-brand-orange rounded-[10px] cursor-pointer snap-start hover:-translate-y-[5px] hover:shadow-[0_8px_20px_rgba(0,0,0,0.15)]" key={item.id}>
                <img src={`https://lasgidirestaurant.com/${item.image}`} alt={item.item_name} className="w-full h-[180px] object-cover" loading="lazy" />
                <div className="py-[15px]">
                  <h3 className="text-[15px] h-20 text-white">{item.item_name}</h3>
                  <p className="text-sm text-white mb-2.5">{item.item_description}</p>
                  <p className="font-bold text-white">
                    <span className="font-normal text-xs text-white">FROM</span><br />
                    £{Number(item.price).toFixed(2)}
                  </p>
                </div>
              </div>
            )
          )}
          {/* Spacer to prevent right border cutoff on scroll */}
          <div className="w-2 flex-shrink-0"></div>
        </div>

        {/* Mobile View Full Menu */}
        <div className="hidden max-[768px]:flex justify-start mt-8 w-full">
          <button
            className="border-2 border-brand-orange bg-transparent text-brand-orange font-semibold text-lg rounded cursor-pointer transition-colors duration-300 px-8 py-3 font-[inherit] hover:bg-[#facc15] hover:text-black hover:border-[#facc15] max-[480px]:w-full max-[480px]:px-4 max-[480px]:py-2.5 max-[480px]:text-base"
            onClick={handleViewMenu}
          >
            View Full Menu
          </button>
        </div>
      </section>

      {/* ===== OUR AIM SECTION ===== */}
      <section className="bg-[#3a3323] text-white py-20 standard-padding my-[50px] flex items-right justify-right relative max-[768px]:py-[60px]">
        <div className="flex justify-end mx-auto gap-10 flex-wrap max-[768px]:flex-col-reverse max-[768px]:gap-[60px] max-[768px]:w-full">
          <img src={cateringImage} alt="catering-img" className="hidden max-[768px]:block w-full h-[340px] object-cover" loading="lazy" />
          <div className="flex flex-col w-[40%] max-[768px]:w-full">
            <p className={highlightTextCls}>Vision</p>
            <h2 className="text-4xl font-poppins mb-4">Street food made luxurious</h2>
            <p className={highlightTextCls}>Mission</p>
            <p className="leading-relaxed text-base">Honouring our heritage through homemade food, friendly service, and a welcoming space that feels like home for every guest.</p>
            <p className={highlightTextCls}>Core Value</p>
            <p className="leading-relaxed text-base">We are committed to using fresh ingredients and preparing every dish with care, consistency, and attention to detail.</p>
            <p className={highlightTextCls}>Corporate Philosophy</p>
            <p className="leading-relaxed text-base">Our corporate philosophy is built on a commitment to quality, integrity, and guest satisfaction. We believe in serving food we are proud of, treating our employees with respect, and creating a positive dining experience through consistent excellence.</p>
            <a href="/order-online" className="max-[480px]:w-full block">
              <button className="bg-brand-orange text-black py-5 px-8 border-none rounded cursor-pointer transition-opacity duration-300 font-semibold font-[inherit] text-lg mt-[30px] w-max hover:opacity-70 max-[480px]:w-full max-[480px]:px-4 max-[480px]:py-3.5 max-[480px]:text-base max-[480px]:text-center">
                Start Your Order
              </button>
            </a>
          </div>
        </div>
        {/* Desktop background image */}
        <div
          className="absolute top-[7%] left-[-5%] w-1/2 h-full bg-no-repeat bg-cover bg-center max-[768px]:hidden"
          style={{ backgroundImage: `url(${cateringImage})` }}
        />
      </section>

      {/* ===== GALLERY SECTION ===== */}
      <section className="bg-black text-white py-[60px] font-poppins standard-padding my-[90px] mb-[30px]" id="gallery">
        <div className="flex flex-wrap justify-between mb-5">
          <h2 className="text-3xl font-bold mb-[30px] font-poppins">Gallery</h2>
          <div className="hidden gap-2.5 max-[900px]:flex">
            <button className={arrowBtnCls.replace("max-[768px]:hidden", "")} onClick={prevGalleryItem} disabled={isPrevGalleryDisabled}>&#10094;</button>
            <button className={arrowBtnCls.replace("max-[768px]:hidden", "")} onClick={nextGalleryItem} disabled={isNextGalleryDisabled}>&#10095;</button>
          </div>
        </div>

        <div
          className="flex flex-wrap gap-5 max-[900px]:flex-nowrap max-[900px]:overflow-x-auto max-[900px]:snap-x max-[900px]:scroll-smooth max-[900px]:pb-2.5 scrollbar-hide"
          ref={galleryWrapperRef}
          onScroll={handleGalleryScroll}
        >
          {images.map((image, index) => (
            <div
              className="flex-[0_0_auto] w-60 h-60 relative cursor-pointer max-[480px]:w-[200px] max-[480px]:h-[200px] max-[900px]:snap-start group"
              key={index}
              onClick={() => setSelectedImage(image.src)}
            >
              <img src={image.src} alt={image.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-[400ms] group-hover:scale-[1.08]" />
              <div className="absolute inset-0 bg-black/30 opacity-0 flex justify-center items-center transition-opacity duration-300 group-hover:opacity-100 group-hover:scale-[1.08]">
                <i className="fa-solid fa-arrow-up-right-from-square text-white text-2xl -translate-x-2.5 transition-transform duration-300 group-hover:translate-x-0" />
              </div>
            </div>
          ))}
        </div>

        {selectedImage && (
          <div className="fixed inset-0 bg-black/90 flex justify-center items-center z-50 cursor-zoom-out" onClick={() => setSelectedImage(null)}>
            <img src={selectedImage} alt="Full View" className="w-auto h-auto max-w-none max-h-none object-contain rounded-[10px] block" />
          </div>
        )}
      </section>

      {/* ===== LOCATIONS SECTION ===== */}
      <section ref={mapSectionRef} className="bg-black text-white py-[60px] relative overflow-hidden standard-padding mb-20" id="locations">
        <h2 className="text-3xl font-bold mb-[30px] text-left font-poppins">Locations</h2>
        <div className="relative w-full h-[530px] rounded-[10px] overflow-hidden max-[900px]:h-[650px] bg-[#111]">
          {showMap && (
            <iframe
              title="Lasgidi Restaurant Location"
              src="https://www.google.com/maps?q=34+Wellington+Road,+Manchester,+Eccles+M30+0NP&output=embed"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0 z-[1]"
            />
          )}
          <div className="absolute top-5 left-5 z-[2] bg-black p-[25px] rounded-[10px] w-[90%] max-w-[300px] shadow-[0_4px_15px_rgba(0,0,0,0.4)] text-[13px] max-[900px]:top-auto max-[900px]:bottom-5">
            <h3 className="text-[21px] font-medium mb-1 font-poppins">Lasgidi Restaurant</h3>
            <p className="flex items-center text-[13px] mb-[25px]">
              <span className="w-5 h-5 bg-[#00c851] rounded-full inline-block mr-1.5" />
              Open until {new Date().getDay() === 0 ? "8PM" : "10PM"}
            </p>
            <p className="text-[13px] leading-relaxed mb-[15px] text-[#bdbcbc]">
              34 Wellington Road, Manchester,<br />Eccles M30 0NP<br />
              Phone: <a href="tel:01615660344" className="text-[#bdbcbc] underline underline-offset-1">0161 566 0344</a>
            </p>
            <div className="mb-5 text-[#bdbcbc]">
              <p className="font-normal mt-5 mb-1 text-white">Collection Hours</p>
              <p>Monday - Saturday: 1PM - 10PM<br />Sundays: 2-8pm regular and 2-7pm on Buffet days (Last Sunday of the month)</p>
            </div>
            <div className="flex flex-wrap items-center gap-[15px]">
              <a href="/contact" className="bg-brand-orange text-black px-5 py-2.5 rounded-md font-semibold no-underline transition-colors duration-300 hover:bg-[#e6b800] max-[480px]:flex-1 max-[480px]:text-center">Contact Us</a>
              <a href="https://www.google.com/maps/dir/?api=1&destination=34+Wellington+Road,+Manchester,+Eccles+M30+0NP" target="_blank" rel="noopener noreferrer" className="text-white font-medium underline transition-colors duration-300 hover:text-[#ffcc00] max-[480px]:flex-1 max-[480px]:text-center">Get directions</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
